import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * A hover panel for explaining a number in place.
 *
 * The existing CScore Key popovers use pure `group-hover` CSS, which works
 * because they sit in open page chrome. These panels hang off cells inside
 * scrolling tables, where an absolutely positioned child gets clipped by the
 * table's own bounds — so this one renders into a portal and positions itself
 * from the trigger's viewport rect instead.
 *
 * Opens on hover and on keyboard focus, so the breakdowns are reachable
 * without a mouse.
 */
export default function HoverCard({ children, panel, width = 280 }) {
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);

  const open = useCallback(() => {
    const el = triggerRef.current;
    if (!el || !panel) return;

    const rect = el.getBoundingClientRect();
    const margin = 8;

    // Prefer sitting to the right of the trigger; flip left when that would
    // run off screen, and clamp so the panel never leaves the viewport.
    let left = rect.right + margin;
    if (left + width > window.innerWidth - margin) {
      left = rect.left - width - margin;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    setCoords({ top: rect.top + rect.height / 2, left });
  }, [panel, width]);

  const close = useCallback(() => setCoords(null), []);

  if (!panel) return children;

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={0}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        className="inline-block cursor-help focus:outline-none focus:ring-2 focus:ring-primary-30 rounded-full"
      >
        {children}
      </span>

      {coords && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width,
            transform: 'translateY(-50%)',
            zIndex: 60,
          }}
          className="bg-white rounded-xl shadow-lg border border-[#E6E6E6] p-4 pointer-events-none"
        >
          {panel}
        </div>,
        document.body
      )}
    </>
  );
}
